/**
 * Core Types for Edge Infrastructure
 *
 * SOLID Principles:
 * - Single Responsibility: Each type has one purpose
 * - Open/Closed: Extend via union types without modifying
 * - Interface Segregation: Small, focused interfaces
 */

import type { EdgeMetadata } from '../core/types/edge-definition-types';

// ============================================================================
// Basic Types
// ============================================================================

export interface Point {
  x: number;
  y: number;
}

/**
 * Type of edge connection (system-level, used for config/toggle/opacity)
 * - automation: Connection between Automation and its Input/Output Databases
 * - relation: Connection between Databases with Relations
 */
export type EdgeType = 'automation' | 'relation';

/**
 * Visual edge type for rendering colors.
 * Splits automation into input/output for distinct coloring.
 */
export type EdgeVisualType =
  | 'automation-input'
  | 'automation-output'
  | 'relation';

/** Visual marker rendered at a semantic edge endpoint. */
export type EdgeEndpointMarker = 'none' | 'dot' | 'arrow' | 'crowsfoot';

/**
 * Available path calculation strategies
 * Extensible: Add new types here for future implementations
 */
export type EdgePathType = 'bezier' | 'rounded-elbow' | 'orthogonal';

/**
 * Available routing strategies
 * Extensible: Add new types here for future implementations
 */
export type EdgeRoutingType = 'direct' | 'avoid-overlap' | 'astar';

/**
 * Anchor position hint for edges
 * Specifies which side of an element the edge should connect to
 */
export type AnchorPosition = 'top' | 'right' | 'bottom' | 'left' | 'auto';

// ============================================================================
// Edge Model
// ============================================================================

/**
 * Represents a visual edge between two canvas elements
 * This is a view-only model - not persisted in YDoc
 */
export interface StreamlainEdge {
  /** Unique identifier for the edge */
  readonly id: string;

  /** Source element ID */
  readonly sourceId: string;

  /** Target element ID */
  readonly targetId: string;

  /** Type of edge connection (system-level) */
  readonly type: EdgeType;

  /** Visual type for rendering colors (derived from type + metadata direction) */
  visualType?: EdgeVisualType;

  /** Semantic metadata from edge derivation (automationId, databaseId, direction, etc.) */
  readonly metadata?: EdgeMetadata;

  // Rendering State (mutable)

  /** Whether the edge is currently visible */
  visible: boolean;

  /** Current opacity (0-1) */
  opacity: number;

  /** Whether the edge is highlighted (hover/focus) */
  highlighted: boolean;

  /** Whether the edge is selected (for deletion) */
  selected: boolean;

  // Computed Properties (set by strategies)

  /** Computed SVG path string */
  path?: string;

  /** Additional waypoints from routing strategy */
  waypoints?: Point[];

  // Anchor Hints (optional, for fixed anchor positions)

  /** Hint for which side of source element to connect from */
  sourceAnchor?: AnchorPosition;

  /** Hint for which side of target element to connect to */
  targetAnchor?: AnchorPosition;

  /** Marker at the data-flow source endpoint. */
  sourceMarker?: EdgeEndpointMarker;

  /** Marker at the data-flow target endpoint. */
  targetMarker?: EdgeEndpointMarker;
}

// ============================================================================
// Configuration
// ============================================================================

/**
 * Configuration for a single edge system (automation or relation)
 */
export interface EdgeSystemConfig {
  /** Whether this edge system is enabled */
  enabled: boolean;

  /** Base opacity when not highlighted (0-100) */
  baseOpacity: number;

  /** Path calculation strategy */
  pathType: EdgePathType;

  /** Routing strategy for avoiding overlaps */
  routingType: EdgeRoutingType;
}

/**
 * State for all edge systems
 */
export interface EdgeSystemState {
  automation: EdgeSystemConfig;
  relation: EdgeSystemConfig;
}

/**
 * Default configuration for edge systems
 */
export const DEFAULT_EDGE_CONFIG: EdgeSystemConfig = {
  enabled: true,
  baseOpacity: 100,
  pathType: 'rounded-elbow',
  routingType: 'direct',
};

/**
 * Default state with both systems enabled
 */
export const DEFAULT_EDGE_STATE: EdgeSystemState = {
  automation: { ...DEFAULT_EDGE_CONFIG },
  relation: { ...DEFAULT_EDGE_CONFIG },
};

// ============================================================================
// Routed Edge (from routing strategy)
// ============================================================================

/**
 * Edge with computed routing waypoints
 */
export interface RoutedEdge extends StreamlainEdge {
  /** Waypoints computed by routing strategy */
  waypoints?: Point[];
}
